import axios from 'axios';
import Cookies from 'js-cookie';

export interface HttpTypedApiResponse<T> {
    success: boolean;
    message: string;
    exception: string;
    collection?: T[];
    entity?: T;
}

export interface HttpTypedApiResult<T> extends HttpTypedApiResponse<T> {
    /** Status HTTP da resposta. `0` quando a requisição não chegou ao servidor. */
    status: number;
}

const baseUrl = process.env.REACT_APP_API_URL;
const getToken = () => Cookies.get('token') || '';
//const getToken = () => 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJDb21wYW55IjoiOGQ5NTQyMWYtZmFmMC00YmMxLWFjMTQtYmZmNDY5NzU4YWI5IiwiRW1wbG95ZWUiOiI0MzA5Mzk5Ny1iMjg5LTRjYTEtOGMxZC02NmIzZDkzNjQyMTkiLCJqdGkiOiI2Nzk2NDZlMy1kMGNhLTRmNDUtYWEzMy1hYWFhYWFhYWFhYWEiLCJpc3MiOiJtZXVhcHAuY29tIiwiYXVkIjoibWV1YXBwLmNvbSIsImV4cCI6MTcwMTIwMTIwMCwibWVudXMiOlsiQ09TVF9DRU5URVJTIiwiSk9CX1RJVExFUyJdfQ.W5Rdxbl_byLQcl6NbMSkXW6mzkO7xhINTdVJdibdHx4';

const defaultResponse = {
    success: false,
    entity: null,
    collection: [],
    message: 'Falha ao processar requisição',
};

export const GET_TYPED_ASYNC = async <T extends any>(url: string): Promise<HttpTypedApiResponse<T>> => {
    return GET_ASYNC(url);
};

export const POST_TYPED_ASYNC = async <T extends any>(url: string, request: any): Promise<HttpTypedApiResponse<T>> => {
    return POST_ASYNC(url, request);
};

export const PUT_TYPED_ASYNC = async <T extends any>(url: string, request: any): Promise<HttpTypedApiResponse<T>> => {
    return PUT_ASYNC(url, request);
};

export const DELETE_TYPED_ASYNC = async <T extends any>(url: string): Promise<HttpTypedApiResponse<T>> => {
    return DELETE_ASYNC(url);
};

export const GET_ASYNC = async (url: string) => {
    try {
        const loggedCompanyId = Cookies.get('loggedCompanyId');

        const response = await axios.get(`${baseUrl}${url}`, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
                LoggedCompanyId: loggedCompanyId,
            },
        });

        if (response.status === 204) {
            return { success: true, collection: [] };
        }

        return response.data || defaultResponse;
    } catch (error: any) {
        return defaultResponse;
    }
};

export const POST_ASYNC = async (url: string, request: any) => {
    try {
        const  loggedCompanyId = Cookies.get('loggedCompanyId');

        const response = await axios.post(`${baseUrl}${url}`, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
                LoggedCompanyId: loggedCompanyId,
            },
        });

        return response.data || defaultResponse;
    } catch (error: any) {
        return defaultResponse;
    }
};

export const PUT_ASYNC = async (url: string, request: any) => {
    try {
        const  loggedCompanyId = Cookies.get('loggedCompanyId');

        const response = await axios.put(`${baseUrl}${url}`, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
                LoggedCompanyId: loggedCompanyId,
            },
        });

        return response.data || defaultResponse;
    } catch (error: any) {
        return defaultResponse;
    }
};

export const DELETE_ASYNC = async (url: string) => {
    try {
        const  loggedCompanyId = Cookies.get('loggedCompanyId');

        const response = await axios.delete(`${baseUrl}${url}`, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
                LoggedCompanyId: loggedCompanyId,
            },
        });

        return response.data || defaultResponse;
    } catch (error: any) {
        return defaultResponse;
    }
};

const isApiEnvelope = <T extends any>(data: unknown): data is HttpTypedApiResponse<T> =>
    typeof data === 'object' && data !== null && 'success' in data;

const buildFailure = <T extends any>(status: number, message?: string): HttpTypedApiResult<T> => ({
    success: false,
    message: message || defaultResponse.message,
    exception: '',
    status,
});

/**
 * POST para os endpoints `api/v1`: preserva o status HTTP e o envelope de erro
 * (os demais helpers descartam ambos e devolvem `defaultResponse`).
 * Não envia `LoggedCompanyId`: empresa e usuário vêm dos claims do ID token.
 */
export const POST_TYPED_RESULT_ASYNC = async <T extends any>(url: string, request: any): Promise<HttpTypedApiResult<T>> => {
    try {
        const response = await axios.post<HttpTypedApiResponse<T>>(`${baseUrl}${url}`, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
                'Content-Type': 'application/json',
            },
        });

        if (!isApiEnvelope<T>(response.data)) {
            return buildFailure<T>(response.status);
        }

        return { ...response.data, status: response.status };
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            const { status, data } = error.response;

            return isApiEnvelope<T>(data) ? { ...data, status } : buildFailure<T>(status);
        }

        return buildFailure<T>(0);
    }
};

export const POST_ASYNC_FILE = async (url: string, request: any) => {
    try {
        const loggedCompanyId = Cookies.get('loggedCompanyId');

        const response = await axios.post(`${baseUrl}${url}`, request, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
                LoggedCompanyId: loggedCompanyId,
            },
            responseType: 'blob',
        });

        return response.data;
    } catch (error: any) {
        console.error("Erro ao baixar arquivo", error);
        return null;
    }
};
